import { Card, Button } from '@atoms/index';
import { formatCurrency } from '@utils/currency';

export interface OrderSummaryItem {
  name: string;
  quantity: number;
  unitPrice: number;
  optionNames?: string[];
  removedIngredientNames?: string[];
  specialInstructions?: string;
}

export interface OrderSummaryProps {
  title?: string;
  items: OrderSummaryItem[];
  subtotal: number;
  deliveryFee: number;
  serviceFee: number;
  promotionDiscount?: number | null;
  appliedPromotionName?: string | null;
  total: number;
  submitLabel?: string;
  onSubmit?: () => void;
  isSubmitting?: boolean;
  submitDisabled?: boolean;
  error?: string | null;
  variant?: 'checkout' | 'detail';
}

const OrderSummary = ({
  title = 'Sipariş Özeti',
  items,
  subtotal,
  deliveryFee,
  serviceFee,
  promotionDiscount,
  appliedPromotionName,
  total,
  submitLabel,
  onSubmit,
  isSubmitting = false,
  submitDisabled = false,
  error,
  variant = 'checkout',
}: OrderSummaryProps) => {
  return (
    <Card className={variant === 'checkout' ? 'sticky top-20' : ''}>
      <h2 className="text-xl font-bold text-gray-900 mb-4">{title}</h2>

      {items.length > 0 && (
        <div className="mb-4 pb-4 border-b border-gray-200 space-y-4">
          {items.map((item, index) => {
            const lineTotal = item.unitPrice * item.quantity;
            return (
              <div
                key={index}
                className="flex justify-between items-start gap-4"
              >
                <div className="flex-1 min-w-0 space-y-1.5">
                  <p className="font-semibold text-gray-900 text-base">
                    {item.quantity}x {item.name}
                  </p>
                  {item.optionNames && item.optionNames.length > 0 && (
                    <ul className="space-y-0.5 text-sm text-gray-700">
                      {item.optionNames.map((opt, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-gray-400 mt-0.5 shrink-0">•</span>
                          <span className="break-words">{opt}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.removedIngredientNames && item.removedIngredientNames.length > 0 && (
                    <ul className="space-y-0.5 text-sm text-red-600">
                      {item.removedIngredientNames.map((name, i) => (
                        <li key={i} className="flex items-start gap-2 font-medium">
                          <span className="mt-0.5 shrink-0">−</span>
                          <span className="break-words">{name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.specialInstructions && (
                    <div className="mt-1.5 rounded-lg bg-gray-50 px-3 py-2 border border-gray-100">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        <span className="font-medium text-gray-600">Not: </span>
                        {item.specialInstructions}
                      </p>
                    </div>
                  )}
                </div>
                <p className="font-semibold text-gray-900 text-base whitespace-nowrap shrink-0">
                  {formatCurrency(lineTotal)}
                </p>
              </div>
            );
          })}
        </div>
      )}

      <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
        <div className="flex justify-between text-sm text-gray-600">
          <span>Ara Toplam</span>
          <span>{formatCurrency(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Teslimat Ücreti</span>
          <span>{formatCurrency(deliveryFee)}</span>
        </div>
        <div className="flex justify-between text-sm text-gray-600">
          <span>Hizmet Bedeli</span>
          <span>{formatCurrency(serviceFee)}</span>
        </div>
        {promotionDiscount != null && promotionDiscount > 0 && (
          <div className="flex justify-between text-sm text-[#2A9D8F] font-medium">
            <span>
              {appliedPromotionName ? `🎉 ${appliedPromotionName}` : 'Kupon / İndirim'}
            </span>
            <span>-{formatCurrency(promotionDiscount)}</span>
          </div>
        )}
      </div>

      <div className="flex justify-between text-lg font-bold text-gray-900 mb-6">
        <span>Toplam {variant === 'checkout' ? '(KDV Dahil)' : ''}</span>
        <span>{formatCurrency(total)}</span>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg">
          {error}
        </div>
      )}

      {submitLabel && (
        <Button
          type={onSubmit ? 'button' : 'submit'}
          variant="primary"
          size="lg"
          fullWidth
          isLoading={isSubmitting}
          disabled={submitDisabled}
          onClick={onSubmit}
        >
          {submitLabel}
        </Button>
      )}
    </Card>
  );
};

export default OrderSummary;
